import { toDate } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { CustomFieldProps } from '../../../interfaces/CustomFieldProps';
import { DynamicField } from '../../../interfaces/DynamicField';
import { DynamicFieldResponse } from '../../../interfaces/DynamicFieldResponse';
import { fieldTypes } from '../../customer/settings/finalReport/templateDialogs/common/fieldTypes';

import { DatePicker } from '~/components/ui/date-picker';
import { Input } from '~/components/ui/input';
import {
  RichTextEditor,
  RichTextEditorContent,
} from '~/components/ui/rich-text-editor';
import { Select, Option } from '~/components/ui/select';
import DescriptionTextField from '~/pages/customer/reports/final-report/common/DescriptionField';
import FieldHeader from '~/pages/customer/reports/final-report/common/FieldHeader';
import TitleField from '~/pages/customer/reports/final-report/common/TitleField';
import { transformToDynamicField } from '~/pages/customer/reports/final-report/helper';

interface GeneratedFieldsProps {
  customValues: DynamicFieldResponse[];
}

interface SmallTextFieldProps {
  info: DynamicField;
}

const SmallTextField = ({ info }: SmallTextFieldProps) => (
  <div className='grow my-2 max-w-md'>
    <FieldHeader title={info.fieldTitle} isMandatory={info.isMandatory} />
    <Input
      variant='soft'
      fullWidth
      placeholder={info.placeholder}
      defaultValue={info.value}
      size='lg'
      disabled
    />
  </div>
);

const DateField = ({ field }: CustomFieldProps) => (
  <div className='grow mb-6 max-w-md'>
    <FieldHeader title={field.fieldTitle} isMandatory={field.isMandatory} />
    <DatePicker
      variant='soft'
      placeholder={field.placeholder}
      selected={field.value ? toDate(field.value) : undefined}
      size='lg'
      disabled
    />
  </div>
);

const BigTextField = ({ field }: CustomFieldProps) => {
  return (
    <div className='mb-6'>
      <FieldHeader title={field.fieldTitle} isMandatory={field.isMandatory} />
      <RichTextEditor defaultValue={field.value} readOnly controlled={false}>
        <div className='h-full flex flex-col overflow-y-auto bg-neutral-100 rounded-md'>
          <RichTextEditorContent />
        </div>
      </RichTextEditor>
    </div>
  );
};

const DropdownField = ({ field }: CustomFieldProps) => {
  const selectedItem = field.value;

  return (
    <div className='grow mb-6 max-w-md'>
      <FieldHeader title={field.fieldTitle} isMandatory={field.isMandatory} />
      <Select variant='soft' size='lg' value={selectedItem} disabled>
        <Option disabled value={''}>
          <FormattedMessage defaultMessage='Select item' />
        </Option>
        {field.dropdownItems.map((item) => (
          <Option value={item} key={item}>
            {item}
          </Option>
        ))}
      </Select>
    </div>
  );
};

const generateField = (field: DynamicField, index: number) => {
  const fieldInfo = field;
  switch (fieldInfo.type) {
    case fieldTypes.title:
      return (
        <TitleField key={fieldInfo.fieldId} field={fieldInfo} index={index} />
      );
    case fieldTypes.descriptionText:
      return (
        <DescriptionTextField
          key={fieldInfo.fieldId}
          field={fieldInfo}
          index={index}
        />
      );
    case fieldTypes.datePicker:
      return (
        <DateField key={fieldInfo.fieldId} field={fieldInfo} index={index} />
      );
    case fieldTypes.dropdown:
      return (
        <DropdownField
          key={fieldInfo.fieldId}
          field={fieldInfo}
          index={index}
        />
      );
    case fieldTypes.bigTextField:
      return (
        <BigTextField key={fieldInfo.fieldId} field={fieldInfo} index={index} />
      );
    case fieldTypes.smallTextField:
      return (
        <div
          className='flex flex-row justify-between items-center gap-2 w-full mb-6'
          key={fieldInfo.fieldId}
        >
          <SmallTextField info={fieldInfo} />
          {fieldInfo.siblings &&
            fieldInfo.siblings.map((sibling) => (
              <SmallTextField
                key={sibling.fieldId}
                info={sibling}
              />
            ))}
        </div>
      );
    default:
      return <></>;
  }
};

const GeneratedFields = ({ customValues }: GeneratedFieldsProps) => {
  const customFieldValues = transformToDynamicField(customValues);
  return (
    <>
      {customFieldValues?.map((field, index) => (
        <div key={field.fieldId}>{generateField(field, index)}</div>
      ))}
    </>
  );
};

export default GeneratedFields;
